import type {
  AdmissionsAIDebugMetadata,
  AdmissionsAIMessageFeedback,
  AdmissionsAssistantFeedbackContext,
  AdmissionsAssistantMessageMetadata,
} from './types';

export type AdmissionsAssistantFeedbackInput = {
  assistantEpisodeId: string;
  assistantMessageId: string;
  route: string;
  routeMetadataToken?: string | null;
  persona: string;
  feedback: AdmissionsAIMessageFeedback;
  reasonCode: 'correct' | 'wrong';
  comment?: string | null;
  projectionPlanHash: string;
  outcomeRef?: string | null;
  ts: string;
};

const FALLBACK_HASH_PREFIX = 'fallback:';

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const toRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const stableStringify = (value: unknown): string => {
  const sortDeep = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map(sortDeep);
    }
    if (!input || typeof input !== 'object') {
      return input;
    }
    const source = input as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(source)
        .sort()
        .map((key) => [key, sortDeep(source[key])])
    );
  };
  return JSON.stringify(sortDeep(value));
};

const fallbackHash = (value: string): string => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return `${FALLBACK_HASH_PREFIX}${Math.abs(hash).toString(16).padStart(8, '0')}`;
};

const buildFallbackProjectionPlanHash = (input: {
  pathname: string;
  routeMetadataToken?: string | null;
  persona: string;
  debug: AdmissionsAIDebugMetadata | null;
}): string => fallbackHash(stableStringify({
  pathname: input.pathname,
  routeMetadataToken: input.routeMetadataToken ?? null,
  persona: input.persona,
  responseDecision: normalizeText(input.debug?.responseDecision),
  methodProjection: toRecord(input.debug?.methodProjection),
  contextEngineering: toRecord(toRecord(input.debug?.contextEngineering)?.plan),
}));

export const generateAssistantEpisodeId = (): string => {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  if (typeof randomUuid === 'string' && randomUuid.length > 0) {
    return randomUuid;
  }
  return `assistant-episode-${Date.now()}`;
};

export const extractProjectionPlanHash = (debug: AdmissionsAIDebugMetadata | null): string | null => {
  const methodProjection = toRecord(debug?.methodProjection);
  const aceContract = toRecord(debug?.aceContract);
  const aceMethodProjection = toRecord(aceContract?.methodProjection);
  const contextEngineering = toRecord(debug?.contextEngineering);
  const contextEngineeringPlan = toRecord(contextEngineering?.plan);
  return (
    normalizeText(methodProjection?.planHash)
    ?? normalizeText(aceMethodProjection?.planHash)
    ?? normalizeText(contextEngineeringPlan?.planHash)
    ?? null
  );
};

export const extractOutcomeRef = (debug: AdmissionsAIDebugMetadata | null): string | null => {
  const methodProjection = toRecord(debug?.methodProjection);
  const contextEngineering = toRecord(debug?.contextEngineering);
  const contextEngineeringPlan = toRecord(contextEngineering?.plan);
  const responseDecision = normalizeText(debug?.responseDecision);
  const planId = normalizeText(methodProjection?.planId)
    ?? normalizeText(contextEngineeringPlan?.planId);
  const capabilityId = normalizeText(methodProjection?.selectedCapabilityId)
    ?? normalizeText(contextEngineeringPlan?.selectedCapabilityId);

  if (responseDecision) {
    return `response:${responseDecision}`;
  }
  if (planId) {
    return `plan:${planId}`;
  }
  if (capabilityId) {
    return `capability:${capabilityId}`;
  }
  return null;
};

export const buildAssistantFeedbackContext = (input: {
  assistantEpisodeId: string;
  pathname: string;
  routeMetadataToken?: string | null;
  persona: string;
  debug: AdmissionsAIDebugMetadata | null;
}): AdmissionsAssistantFeedbackContext => {
  const projectionPlanHash = extractProjectionPlanHash(input.debug)
    ?? buildFallbackProjectionPlanHash(input);

  return {
    assistantEpisodeId: input.assistantEpisodeId,
    route: input.pathname,
    routeMetadataToken: input.routeMetadataToken ?? null,
    persona: input.persona,
    projectionPlanHash,
    outcomeRef: extractOutcomeRef(input.debug),
  };
};

export const buildAssistantFeedbackInput = (input: {
  assistantMessageId: string;
  feedback: AdmissionsAIMessageFeedback;
  metadata: AdmissionsAssistantMessageMetadata | null | undefined;
  submittedAt?: string;
}): { ok: true; value: AdmissionsAssistantFeedbackInput } | { ok: false; error: string } => {
  const feedbackContext = input.metadata?.feedbackContext;
  if (!feedbackContext) {
    return { ok: false, error: 'Feedback context is missing for this assistant response.' };
  }
  if (!normalizeText(feedbackContext.projectionPlanHash)) {
    return { ok: false, error: 'Feedback lineage is incomplete because the projection plan hash is missing.' };
  }

  return {
    ok: true,
    value: {
      assistantEpisodeId: feedbackContext.assistantEpisodeId,
      assistantMessageId: input.assistantMessageId,
      route: feedbackContext.route,
      routeMetadataToken: feedbackContext.routeMetadataToken ?? null,
      persona: feedbackContext.persona,
      feedback: input.feedback,
      reasonCode: input.feedback === 'up' ? 'correct' : 'wrong',
      projectionPlanHash: feedbackContext.projectionPlanHash,
      outcomeRef: feedbackContext.outcomeRef ?? null,
      ts: input.submittedAt ?? new Date().toISOString(),
    },
  };
};

export const isQueueableFeedbackError = (error: unknown): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  const message = String(error instanceof Error ? error.message : error ?? '').toLowerCase();
  if (!message) {
    return false;
  }
  return (
    message.includes('network')
    || message.includes('fetch')
    || message.includes('timed out')
    || message.includes('timeout')
    || message.includes('status 502')
    || message.includes('status 503')
    || message.includes('status 504')
  );
};
